import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Router } from "@vaadin/router";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/textarea/textarea.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import { flowStyles } from "../styles/flow-styles";
import { loadUserSettings, saveFocusedEntry } from "../utils/entry-flow";
import { DEFAULT_SETTINGS, SICK_STATUS_INFO } from "../types";
import type { SickStatus, UserSettings } from "../types";

/**
 * Focused flow: mark a sick day or recovery. No GL required. See
 * docs/add-task-flows.md.
 */
@customElement("add-sick-page")
export class AddSickPage extends LitElement {
  @state() private status: SickStatus | null = null;
  @state() private symptoms = "";
  @state() private isSaving = false;
  @state() private showSaved = false;
  @state() private settings: UserSettings = { ...DEFAULT_SETTINGS };

  static styles = [
    flowStyles,
    css`
      .field {
        margin-bottom: 1.25rem;
      }
      .field-label {
        font-size: 0.875rem;
        font-weight: 600;
        color: #495057;
        margin-bottom: 0.5rem;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    void this._load();
  }

  private async _load() {
    this.settings = await loadUserSettings();
  }

  private _handleBack() {
    Router.go("/");
  }

  private async _handleSave() {
    if (!this.status) return;

    this.isSaving = true;
    try {
      const symptoms = this.symptoms.trim();
      await saveFocusedEntry(
        {
          sickDay: {
            status: this.status,
            ...(symptoms ? { symptoms } : {}),
          },
        },
        this.settings,
      );
      this.showSaved = true;
      setTimeout(() => Router.go("/"), 500);
    } catch (err) {
      console.error("Failed to save sick day:", err);
      this.isSaving = false;
    }
  }

  render() {
    const canSave = !!this.status && !this.isSaving;

    return html`
      <div class="page-container">
        ${this.showSaved
          ? html`
              <wa-callout variant="success" open class="success-banner">
                <wa-icon name="circle-check" slot="icon"></wa-icon>
                Saved!
              </wa-callout>
            `
          : nothing}

        <div class="page-header">
          <button class="back-btn" @click=${this._handleBack} aria-label="Back">
            <wa-icon name="arrow-left"></wa-icon>
          </button>
          <h1>Sick day</h1>
        </div>

        <div class="form-section">
          <div class="field">
            <div class="field-label">How are you?</div>
            <div class="chip-row">
              ${(Object.keys(SICK_STATUS_INFO) as SickStatus[]).map(
                (s) => html`
                  <button
                    class="chip ${this.status === s ? "selected" : ""}"
                    @click=${() => (this.status = s)}
                  >
                    <wa-icon name=${SICK_STATUS_INFO[s].icon}></wa-icon>
                    ${SICK_STATUS_INFO[s].label}
                  </button>
                `,
              )}
            </div>
          </div>

          <div class="field">
            <div class="field-label">Symptoms (optional)</div>
            <wa-textarea
              placeholder="e.g. nausea, fever, cold"
              rows="2"
              .value=${this.symptoms}
              @input=${(e: Event) =>
                (this.symptoms = (e.target as HTMLTextAreaElement).value)}
            ></wa-textarea>
          </div>

          ${this.status === "sick"
            ? html`
                <wa-callout variant="warning" open>
                  <wa-icon name="face-thermometer" slot="icon"></wa-icon>
                  <strong>Sick day rules</strong>
                  <p style="margin: 0.5rem 0 0 0;">
                    Based on OZDAFNE guidelines: never skip your insulin even if
                    not eating, check glucose every 2-4 hours, check ketones if
                    glucose is high, and keep up fluids.
                  </p>
                </wa-callout>
              `
            : nothing}

          <div class="actions">
            <wa-button
              class="save-btn"
              variant="brand"
              size="large"
              ?disabled=${!canSave}
              ?loading=${this.isSaving}
              @click=${this._handleSave}
            >
              ${this.isSaving ? "Saving..." : "Save"}
            </wa-button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "add-sick-page": AddSickPage;
  }
}
